import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { Observable, map, combineLatest } from 'rxjs';
import { BaseRepository } from '../../../../core/state/base.repository';
import { ICompletionState, IRoleType, IUserProfileInitial } from '../../../../shared/models/profiles/user-profile';
import { UserState } from '@shared/models/profiles/user-state';

export interface IProfileInitialState {
  profileInitial: IUserProfileInitial | null;
  loading: boolean;
}

const initialState: IProfileInitialState = {
  profileInitial: null,
  loading: false,
};

const profileInitialStore = createStore(
  { name: 'profileInitial' },
  withProps<IProfileInitialState>(initialState)
);

export const profileInitialPersist = persistState(profileInitialStore, {
  key: 'profileInitial',
  storage: localStorageStrategy,
});

@Injectable({ providedIn: 'root' })
export class ProfileInitialRepository extends BaseRepository<IProfileInitialState> {
  profileInitial$: Observable<IUserProfileInitial | null> = profileInitialStore.pipe(
    select((st) => st.profileInitial)
  );
  
  role$: Observable<IRoleType | null> = profileInitialStore.pipe(
    select((st) => st.profileInitial?.userRole ?? null)
  );

  loading$: Observable<boolean> = profileInitialStore.pipe(
    select((state) => state.loading)
  );

  avatarId$: Observable<string | null> = profileInitialStore.pipe(
    select((st) => st.profileInitial?.avatarId ?? null)
  );

  completionState$: Observable<ICompletionState | null> = profileInitialStore.pipe(
    select((st) => st.profileInitial?.completionState as ICompletionState | null)
  );

  userState$: Observable<UserState | null> = profileInitialStore.pipe(
    select((st) => st.profileInitial?.userState as UserState | null)
  );
  
  /**
   * Observable that indicates if the profile is sufficiently complete
   * A profile is considered complete when basic info, contacts, and specific info are all filled
   */
  isProfileComplete$: Observable<boolean> = this.completionState$.pipe(
    map((completionState) => {
      if (!completionState) return false;
      
      return completionState.isBasicInfoCompleted && 
             completionState.isContactsCompleted && 
             completionState.isSpecificInfoCompleted &&
             completionState.isLegalInfoCompleted;
    })
  );

  constructor() {
    super('profileInitial', initialState);
  }

  setLoading(loading: boolean): void {
    profileInitialStore.update((state) => ({ ...state, loading }));
  }

  setProfileInitial(profileInitial: IProfileInitialState['profileInitial']): void {
    profileInitialStore.update((st) => ({ ...st, profileInitial }));
  }
  
  /**
   * Check if a profile is complete based on completion state
   * @param completionState The completion state to check
   * @returns True if the profile is complete (basic info, contacts, and specific info all filled), false otherwise
   */
  isProfileComplete(completionState: ICompletionState | null): boolean {
    if (!completionState) return false;
    
    return completionState.isBasicInfoCompleted && 
           completionState.isContactsCompleted && 
           completionState.isSpecificInfoCompleted;
  }

  /**
   * Calculate the completion percentage based on the completion state
   * 
   * @param completionState The completion state to check
   * @param userRole The user's role (teacher or student)
   * @returns A number from 0 to 100 representing completion percentage
   */
  calculateCompletionPercentage(completionState: ICompletionState | null, userRole: IRoleType | null): number {
    if (!completionState || !userRole) return 0;
    
    // Define required items based on user role
    const requiredItems = [
      completionState.isBasicInfoCompleted, 
      completionState.isContactsCompleted,
      completionState.isSpecificInfoCompleted
    ];
    
    // Add teacher-specific requirements
    if (userRole === 'teacher') {
      requiredItems.push(
        completionState.isLegalInfoCompleted,
        completionState.isSpecificInfoCompleted
      );
    }
    
    // Calculate percentage
    const totalItems = requiredItems.length;
    const completedItems = requiredItems.filter(item => item).length;
    return Math.round((completedItems / totalItems) * 100);
  }
  
  /**
   * Observable that provides the completion percentage
   */
  completionPercentage$ = combineLatest([
    this.completionState$,
    this.role$
  ]).pipe(
    map(([completionState, role]) => 
      this.calculateCompletionPercentage(completionState, role)
    )
  );
}